import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  ButtonBase,
  EmptyState,
  Field,
  FormLabel,
  IconButton,
  ModalDialogBody,
  ModalDialogHeader,
  ModalDialogRoot,
  ServiceIcon,
  SkeletonBlock,
  Spinner,
  Tag,
  Text,
  TextInput,
  Tooltip,
  VisuallyHidden,
} from "@zapier/design-system";

import { Icon } from "@zapier/zinnia-icons";

import styles from "./AddConnectionModal.module.css";

import { debounce, PLACEHOLDER_APP_ICON, useNotification } from "@zapier/utils";
import {
  App,
  useHydrateImplementationsWithImplementationsMeta,
  useQueryImplementationsMeta,
  useRefetchAuthentications,
  useRefetchPublicAndPrivateApps,
} from "@zapier/client";
import {
  ManagedConnectionSource,
  useEmitManagedConnectionCreateAbandonedEvent,
  useEmitManagedConnectionCreatedEvent,
} from "@zapier/shared";
import { useTranslations } from "@zapier/i18n";
import useCreateAppConnection from "../useCreateAppConnection";
import { useConnectionCreateRules } from "../useConnectionCreateRules";
import { ConnectionCreateBlocked } from "../ConnectionCreateBlocked";
import { ConnectionErrorCallbackPayload } from "../types";
import {
  NewManagedConnection,
  NewManagedConnectionStep,
} from "./NewManagedConnectionStep";

const SKELETON_ROW_COUNT = 6;
const RESULTS_DIALOG_HEIGHT = 560;

interface AddConnectionModalProps {
  setIsOpen: (isOpen: boolean) => void;
  isOpen: boolean;
  mode?: "personal" | "managed";
  onConnectionError?: (error: ConnectionErrorCallbackPayload) => void;
  // When set, seeds the "Select app" search-and-list step with this term so
  // it opens straight to the matched app instead of an empty list. Does not
  // auto-connect - the user still has to click the row like any other entry
  // point into the managed-connection flow.
  preselectedAppName?: string;
  // Required for mode="managed" — attributes created/abandoned events.
  source?: ManagedConnectionSource;
}

const AddConnectionModal = ({
  setIsOpen,
  isOpen,
  mode = "personal",
  onConnectionError,
  preselectedAppName,
  source,
}: AddConnectionModalProps) => {
  const t = useTranslations("add-connection-modal");
  const [appSearch, setAppSearch] = useState("");
  const [inputValue, setInputValue] = useState(preselectedAppName ?? "");
  const [connectingAppId, setConnectingAppId] = useState<string | undefined>();
  const [newManagedConnection, setNewManagedConnection] = useState<
    NewManagedConnection | undefined
  >();

  const { emit: emitManagedConnectionCreated } =
    useEmitManagedConnectionCreatedEvent();
  const { emit: emitManagedConnectionCreateAbandoned } =
    useEmitManagedConnectionCreateAbandonedEvent();
  // Prevents a completed close from also firing an abandoned event.
  const hasCompletedManagedConnectionRef = useRef(false);

  const onAppSearch = useMemo(
    () =>
      debounce((inputValue: string) => {
        setAppSearch(inputValue || "");
      }, 500),
    [setAppSearch],
  );

  // Reset all step/search/selection state on every open so a prior session's
  // search term, results, and step don't linger when the modal is reopened.
  // When a preselectedAppName is provided, seed the search with it so the
  // implementation-meta lookup below starts immediately.
  useEffect(() => {
    if (!isOpen) return;
    onAppSearch.cancel();
    setAppSearch(preselectedAppName ?? "");
    setInputValue(preselectedAppName ?? "");
    setConnectingAppId(undefined);
    setNewManagedConnection(undefined);
    hasCompletedManagedConnectionRef.current = false;
  }, [isOpen, preselectedAppName, onAppSearch]);

  // Single choke point for every way this dialog can close. By the time
  // newManagedConnection is set, the connection already exists server-side
  // (step 1 returned an id) — closing without sharing is still a creation,
  // not an abandonment, since nothing deletes that connection.
  const handleClose = useCallback(
    (open: boolean) => {
      if (
        !open &&
        mode === "managed" &&
        source &&
        !hasCompletedManagedConnectionRef.current
      ) {
        hasCompletedManagedConnectionRef.current = true;
        if (newManagedConnection) {
          emitManagedConnectionCreated(source, { isShared: false });
        } else {
          emitManagedConnectionCreateAbandoned(source);
        }
      }
      setIsOpen(open);
    },
    [
      mode,
      source,
      newManagedConnection,
      emitManagedConnectionCreated,
      emitManagedConnectionCreateAbandoned,
      setIsOpen,
    ],
  );

  // Blocked apps render as locked rows; domain restriction is informational only.
  const {
    getCreateBlock,
    getDomainRestriction,
    isLoading: isCreateRulesLoading,
    isManagedGuardsError,
  } = useConnectionCreateRules({ enabled: isOpen });

  const { createNotification } = useNotification();

  useEffect(() => {
    if (isManagedGuardsError && isOpen) {
      createNotification({
        message: t("managed.error"),
        type: "error",
      });
    }
  }, [isManagedGuardsError, isOpen, createNotification, t]);

  const {
    implementationsMeta,
    isLoading: isImplementationsMetaLoading,
    isFetching: isImplementationsMetaFetching,
    isPlaceholderData: isImplementationsMetaPlaceholder,
  } = useQueryImplementationsMeta({
    term: appSearch,
  });

  const { refetchAuthentications } = useRefetchAuthentications();
  const { refetchPublicAndPrivateApps } = useRefetchPublicAndPrivateApps();

  const handleAppSearch = (value: string) => {
    if (value !== inputValue) {
      setInputValue(value);
      onAppSearch(value);
    }
  };

  const handleClearAppSearch = () => {
    onAppSearch.cancel();
    setInputValue("");
    setAppSearch("");
  };

  const isSearchPending = inputValue !== appSearch;
  const isSearchingNewTerm =
    isImplementationsMetaFetching && isImplementationsMetaPlaceholder;

  const { hydratedImplementationMetas, isHydrating } =
    useHydrateImplementationsWithImplementationsMeta({
      searchedImplementationMetas: implementationsMeta,
      onlyAppsWithAuth: true,
    });

  const createNewConnection = useCreateAppConnection();

  const managedAppOptions = useMemo(() => {
    // useQueryImplementationsMeta disables its query when appSearch is
    // empty but keeps the last-fetched data (keepPreviousData); the list
    // stays visible, so it must not render that stale leftover.
    if (!appSearch) return [];
    return hydratedImplementationMetas;
  }, [appSearch, hydratedImplementationMetas]);

  const connectListApp = useCallback(
    async (app: (typeof managedAppOptions)[number]) => {
      const appId = String(app.id ?? "");
      const listApp = app as unknown as App;
      setConnectingAppId(appId);
      const result = await createNewConnection(
        listApp,
        mode === "managed" ? { isManaged: true } : undefined,
      );
      setConnectingAppId(undefined);

      if (result.connectionError) {
        onConnectionError?.({
          appId,
          errorDescription: result.connectionError.errorDescription,
        });
        handleClose(false);
        return;
      }

      // `success` is the authoritative signal, not `id` — some OAuth apps
      // never return one even on a genuine success (see
      // useCreateAppConnection). Only advancing to the managed rename/share
      // step actually needs the id; a successful-but-id-less connect should
      // still refetch and notify rather than close with no feedback.
      if (result.success) {
        refetchAuthentications();
        refetchPublicAndPrivateApps();
        if (mode === "managed" && result.id) {
          // Advances to the rename/share step within this same modal — do
          // not close here, that would end the flow before it's configured.
          setNewManagedConnection({
            id: Number(result.id),
            appName: app.name || "",
            appImage: app.image,
          });
          return;
        }
        if (mode === "managed" && source) {
          // No id means no rename/share step to advance to — already complete.
          hasCompletedManagedConnectionRef.current = true;
          emitManagedConnectionCreated(source, { isShared: false });
        }
        createNotification({
          message: t("notifications.connection-added-success", {
            appName: app.name || "",
          }),
          type: "success",
        });
        setIsOpen(false);
        return;
      }
      handleClose(false);
    },
    [
      createNewConnection,
      createNotification,
      emitManagedConnectionCreated,
      handleClose,
      mode,
      onConnectionError,
      refetchAuthentications,
      refetchPublicAndPrivateApps,
      setIsOpen,
      source,
      t,
    ],
  );

  const renderAppList = () => {
    if (
      isSearchPending ||
      isImplementationsMetaLoading ||
      isHydrating ||
      isSearchingNewTerm
    ) {
      return (
        <div className={styles.managedAppListLoading}>
          {Array.from({ length: SKELETON_ROW_COUNT }, (_, index) => (
            <div key={index} className={styles.managedAppListLoadingItem}>
              <SkeletonBlock height={24} width={24} />
              <SkeletonBlock height={14} width="60%" />
            </div>
          ))}
        </div>
      );
    }

    if (!appSearch) return null;

    if (managedAppOptions.length === 0) {
      return (
        <div className={styles.emptyState}>
          <EmptyState
            size="minimal"
            variant="flat"
            title={t("empty-state.no-apps")}
            paragraph={t("empty-state.no-apps-description")}
          />
        </div>
      );
    }

    return managedAppOptions.map((app) => {
      const appId = String(app.id ?? "");
      const appName = app.name || "";

      const createBlock = getCreateBlock(appId, {
        isManaged: mode === "managed",
      });
      if (createBlock) {
        return (
          <div key={appId} className={styles.managedAppRowRestricted}>
            <ConnectionCreateBlocked
              reason={createBlock}
              triggerClassName={styles.managedAppRowRestrictedInfo}
            >
              <span className={styles.managedAppRowRestrictedApp}>
                <ServiceIcon
                  serviceName={appName}
                  size="x-small"
                  src={app.image || PLACEHOLDER_APP_ICON}
                />
                <Text type="Body3" tag="span">
                  {appName}
                </Text>
              </span>
            </ConnectionCreateBlocked>
          </div>
        );
      }

      // Domain restriction is non-blocking — shown as an informational badge,
      // with the domain list in the row's tooltip.
      const domainRestriction = getDomainRestriction(appId, "create");

      const isConnecting = connectingAppId === appId;

      const row = (
        <ButtonBase
          key={appId}
          variant="tertiary"
          size="large"
          className={styles.managedAppRow}
          isPending={isConnecting}
          isDisabled={
            (connectingAppId !== undefined && connectingAppId !== appId) ||
            isCreateRulesLoading
          }
          // Overrides the name only while connecting, when the spinner replaces the visible text.
          aria-label={isConnecting ? appName : undefined}
          onPress={() => void connectListApp(app)}
        >
          {isConnecting ? (
            <span className={styles.managedAppRowSpinner}>
              {/* aria-label="" silences Spinner's default label; the button's own aria-label above already names it. */}
              <Spinner size="small" aria-label="" />
            </span>
          ) : (
            <span className={styles.managedAppRowContent}>
              <ServiceIcon
                serviceName={appName}
                size="x-small"
                src={app.image || PLACEHOLDER_APP_ICON}
              />
              <Text type="Body3" tag="span">
                {appName}
              </Text>
              {domainRestriction && (
                <Tag variant="simple">
                  <span className={styles.managedBadgeIcon}>
                    <Icon name="miscDomain" aria-hidden={true} />
                  </span>
                  {t("domain-restricted.badge")}
                </Tag>
              )}
            </span>
          )}
        </ButtonBase>
      );

      if (!domainRestriction) return row;

      return (
        <Tooltip
          key={appId}
          content={t("domain-restricted.tooltip", {
            mode: domainRestriction.mode,
            domains: domainRestriction.domains.join(", "),
          })}
          allowMultiline
          placement="top"
        >
          {row}
        </Tooltip>
      );
    });
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  const headerTitle = t("header.title-list", { mode });

  return (
    <ModalDialogRoot
      dialogClassName={styles.modalDialog}
      height={inputValue ? RESULTS_DIALOG_HEIGHT : "auto"}
      isOpen={true}
      onOpenChange={handleClose}
      aria-label={t("header.aria-label", {
        step: newManagedConnection ? "rename" : mode,
      })}
    >
      {newManagedConnection ? (
        <NewManagedConnectionStep
          connection={newManagedConnection}
          onCancel={() => handleClose(false)}
          onComplete={(isShared) => {
            hasCompletedManagedConnectionRef.current = true;
            if (source) {
              emitManagedConnectionCreated(source, { isShared });
            }
            setIsOpen(false);
          }}
        />
      ) : (
        <>
          <ModalDialogHeader textAlign="start">{headerTitle}</ModalDialogHeader>
          <ModalDialogBody textAlign="start" className={styles.modalBody}>
            <div className={styles.appSearch}>
              <Field
                label={t("labels.app")}
                inputId={
                  mode === "managed"
                    ? "add-managed-connection-search"
                    : "add-connection-search"
                }
                renderLabel={(labelProps) => (
                  <VisuallyHidden>
                    <FormLabel {...labelProps}>{t("labels.app")}</FormLabel>
                  </VisuallyHidden>
                )}
                renderInput={(inputProps) => (
                  <TextInput
                    {...inputProps}
                    aria-label={t("labels.app")}
                    placeholder={t("placeholders.search-apps")}
                    value={inputValue}
                    onChange={(event) => handleAppSearch(event.target.value)}
                    renderIconBefore={() => (
                      <Icon name="navSearch" aria-hidden={true} />
                    )}
                    renderIconAfter={(iconProps) => (
                      <IconButton
                        {...iconProps}
                        name="formX"
                        aria-label={t("labels.clear-search")}
                        variant="ghost"
                        size="small"
                        onPress={handleClearAppSearch}
                      />
                    )}
                    isFullWidth={true}
                    size="medium"
                  />
                )}
              />
            </div>
            <div
              className={styles.managedAppList}
              data-results={inputValue ? "true" : "false"}
            >
              <Text type="Body3" tag="span">
                {t("list.results")}
              </Text>
              {renderAppList()}
            </div>
          </ModalDialogBody>
        </>
      )}
    </ModalDialogRoot>
  );
};

export default AddConnectionModal;
